import { AboutProjectStep } from './components/AboutProjectStep';
import { BlueprintStepTemplate } from './components/BlueprintStepTemplate';
import { BusinessCaseStep } from './components/BusinessCaseStep';
import { PowerRequirementsStep } from './components/PowerRequirementsStep';
import { BlueprintFormData } from './types';
import { mapBlueprintToFormData } from './utils/blueprintFormMapper';
import { RentalSource } from './utils/constants';
import { buildUpdatePayload } from './utils/payloadBuilder';
import { createBlueprintSchema } from './validation';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError, AxiosResponse } from 'axios';
import { useCreateBlueprint } from 'features/blueprints/hooks/useCreateBlueprint/useCreateBlueprint';
import { useUpdateBlueprint } from 'features/blueprints/hooks/useUpdateBlueprint/useUpdateBlueprint';
import { powerProfileModes } from 'greener-ui-library';
import useAuth from 'hooks/useAuth';
import { useGetEquipment } from 'hooks/useEquipment/useGetEquipment';
import { ComponentType, useState } from 'react';
import { Resolver } from 'react-hook-form';
import { FormProvider, UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Blueprint, BlueprintFromApi } from 'types';

interface WizardStepProps {
  formMethods: UseFormReturn<BlueprintFormData>;
  blueprintId?: number;
  mode?: 'create' | 'edit';
  onPowerProfileResetChange?: (hasClickedReset: boolean) => void;
}

interface WizardStep {
  id: number;
  component: ComponentType<WizardStepProps>;
  label: string;
  isFirst?: boolean;
  isLast?: boolean;
  requiredFields: Array<keyof BlueprintFormData>;
}

interface BlueprintWizardProps {
  mode?: 'create' | 'edit';
  blueprintId?: number;
  blueprintDetails?: Blueprint;
  initialStep?: number;
}

export const BlueprintWizard = ({
  mode = 'create',
  blueprintId,
  blueprintDetails,
  initialStep = 0,
}: BlueprintWizardProps = {}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStep);
  const [hasUserClickedPowerProfileReset, setHasUserClickedPowerProfileReset] = useState(false);
  const [, setFieldErrors] = useState<Record<string, string>>();
  const { createBlueprint, isLoading: isCreatingBlueprint } = useCreateBlueprint();
  const { updateBlueprint, isLoading: isUpdatingBlueprint } = useUpdateBlueprint();

  const getDefaultValues = (): BlueprintFormData => {
    const baseDefaults: BlueprintFormData = {
      blueprintName: '',
      clientName: '',
      applicationType: '',
      startDate: new Date(),
      endDate: undefined,
      powerProfileMode: powerProfileModes.manual,
      peakPowerKw: undefined,
      averagePowerKw: undefined,
      peaksPerDay: undefined,
      peaksDuration: undefined,
      restDayAveragePowerKw: undefined,
      workingDays: [],
      workingHoursStart: '08:00',
      workingHoursEnd: '15:00',
      gensetKva: '',
      gensetCount: '',
      mainsConnection: false,
      maxAmpsAvailable: undefined,
      currency: 'EUR',
      gensetRentalSource: RentalSource.CATALOG,
      gensetType: '',
      hybridGensetKva: undefined,
      gensetRentalCount: undefined,
      gensetRentalCost: undefined,
      batteryRentalSource: RentalSource.CATALOG,
      batteryModel: '',
      batterySize: undefined,
      converterPower: undefined,
      batteryRentalCount: 1,
      batteryRentalCost: undefined,
      hybridDieselPricePerLiter: 0,
      hybridElectricityPricePerKwh: undefined,
      hybridAdditionalCostsPerWeek: undefined,
      conventionalDieselPricePerLiter: undefined,
      conventionalGensetRentalCostPerWeek: undefined,
      conventionalAdditionalCostsPerWeek: undefined,
    };

    if (mode === 'edit' && blueprintDetails) {
      const mappedData = mapBlueprintToFormData(blueprintDetails);
      return {
        ...baseDefaults,
        ...mappedData,
      } as BlueprintFormData;
    }

    return baseDefaults;
  };

  const organisationId = user?.organisation?.organisationId?.toString();
  const { equipment: gensetEquipment } = useGetEquipment(organisationId || '');

  const methods = useForm<BlueprintFormData>({
    mode: 'onBlur',
    resolver: yupResolver(
      createBlueprintSchema(mode === 'edit', gensetEquipment)
    ) as unknown as Resolver<BlueprintFormData>,
    defaultValues: getDefaultValues(),
  });

  const WIZARD_STEPS: WizardStep[] = [
    {
      id: 0,
      component: AboutProjectStep,
      label: t('blueprints:wizard.steps.aboutProject'),
      isFirst: true,
      requiredFields: ['blueprintName', 'clientName', 'startDate'],
    },
    {
      id: 1,
      component: PowerRequirementsStep,
      label: t('blueprints:wizard.steps.powerRequirements'),
      requiredFields: [
        'peakPowerKw',
        'averagePowerKw',
        'peaksPerDay',
        'peaksDuration',
        'restDayAveragePowerKw',
        'workingDays',
        'workingHoursStart',
        'workingHoursEnd',
        'mainsConnection',
        'maxAmpsAvailable',
      ],
    },
    {
      id: 2,
      component: BusinessCaseStep,
      label: t('blueprints:wizard.steps.businessCase'),
      isLast: true,
      requiredFields: [
        'converterPower',
        'batterySize',
        'currency',
        'gensetRentalSource',
        'gensetKva',
        'gensetCount',
        'gensetType',
        'gensetRentalCount',
        'gensetRentalCost',
        'batteryRentalSource',
        'batteryModel',
        'batteryRentalCount',
        'batteryRentalCost',
        'hybridDieselPricePerLiter',
        'hybridElectricityPricePerKwh',
        'conventionalDieselPricePerLiter',
        'conventionalGensetRentalCostPerWeek',
      ],
    },
  ];

  const currentStep = WIZARD_STEPS[currentStepIndex];
  const CurrentStepComponent = currentStep.component;

  const handleStepValidation = async () => {
    let fieldsToValidate = [...currentStep.requiredFields];

    // For step 2 (Power Requirements), conditionally validate fields based on power profile mode
    if (currentStepIndex === 1) {
      if (mode === 'edit' && !hasUserClickedPowerProfileReset) {
        // Skip validation entirely - user hasn't reset, so form data comes from API
        handleNext(methods.getValues());
        return true;
      }

      const powerProfileMode = methods.getValues('powerProfileMode');

      if (powerProfileMode === powerProfileModes.file) {
        // In file mode, only validate mains fields and CSV upload
        fieldsToValidate = ['mainsConnection', 'maxAmpsAvailable', 'rawPowerProfileCsv'] as Array<
          keyof BlueprintFormData
        >;
      }
      // In manual mode, keep all required fields as defined
    }

    // For step 3 (Business Case), conditionally validate fields based on user selections
    if (currentStepIndex === 2) {
      const mainsConnection = methods.getValues('mainsConnection');
      const gensetRentalSource = methods.getValues('gensetRentalSource');
      const batteryRentalSource = methods.getValues('batteryRentalSource');

      // Ensure gensetKva and gensetCount are included
      if (!fieldsToValidate.includes('gensetKva')) {
        fieldsToValidate.push('gensetKva');
      }
      if (!fieldsToValidate.includes('gensetCount')) {
        fieldsToValidate.push('gensetCount');
      }

      // Touch gensetKva and gensetCount before validation
      methods.setValue('gensetKva', methods.getValues('gensetKva'), { shouldTouch: true });
      methods.setValue('gensetCount', methods.getValues('gensetCount'), { shouldTouch: true });

      // Remove hybridElectricityPricePerKwh if no mains connection
      if (!mainsConnection) {
        fieldsToValidate = fieldsToValidate.filter((field) => field !== 'hybridElectricityPricePerKwh');
      }

      // Remove gensetType if manual mode (not catalog), add hybridGensetKva validation
      if (gensetRentalSource === RentalSource.MANUAL) {
        fieldsToValidate = fieldsToValidate.filter((field) => field !== 'gensetType');
        if (!fieldsToValidate.includes('hybridGensetKva')) {
          fieldsToValidate.push('hybridGensetKva');
        }
      } else {
        fieldsToValidate = fieldsToValidate.filter((field) => field !== 'hybridGensetKva');
      }

      // Remove batteryModel if manual mode (not catalog)
      if (batteryRentalSource === RentalSource.MANUAL) {
        fieldsToValidate = fieldsToValidate.filter((field) => field !== 'batteryModel');
      }

      // Remove genset fields if no genset mode
      if (gensetRentalSource === RentalSource.NONE) {
        const fieldsToRemove = ['gensetType', 'gensetRentalCount', 'gensetRentalCost'];
        fieldsToValidate = fieldsToValidate.filter((field) => !fieldsToRemove.includes(field));
      }
    }

    const isValid = await methods.trigger(fieldsToValidate);

    // For the first step, also validate endDate if it's filled
    if (currentStepIndex === 0) {
      const endDateIsValid = await methods.trigger('endDate');
      if (!endDateIsValid) {
        return false;
      }
    }

    if (isValid) {
      handleNext(methods.getValues());
    }
    return isValid;
  };

  const handleNext = async (data: BlueprintFormData) => {
    if (currentStepIndex < WIZARD_STEPS.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      try {
        if (mode === 'edit' && blueprintId) {
          // Edit mode - update blueprint
          const updatePayload = buildUpdatePayload(data);

          await updateBlueprint({
            blueprintId,
            data: updatePayload,
          });
          navigate(`/solutions/${blueprintId}`);
        } else {
          // Create mode
          const response = (await createBlueprint(data)) as unknown as AxiosResponse<BlueprintFromApi>;
          const responseData = response?.data;
          const newBlueprintId = responseData?.blueprint_id;

          if (newBlueprintId) {
            navigate(`/solutions/${newBlueprintId}`);
          } else {
            console.warn('No blueprint_id found in response, navigating to list');
            navigate('/solutions');
          }
        }
      } catch (error) {
        console.error(`Error during ${mode === 'edit' ? 'updating' : 'creating'} Blueprint:`, error);
        const axiosError = error as AxiosError<{ errors: Record<string, string>; detail?: string; message?: string }>;

        if (axiosError.response?.data?.errors) {
          setFieldErrors(axiosError.response.data.errors);
        }
      }
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  const handleCancel = () => {
    if (mode === 'edit' && blueprintId) {
      navigate(`/solutions/${blueprintId}`);
    } else {
      navigate('/solutions');
    }
  };

  const handleSkipAndSave = async () => {
    if (mode === 'edit' && blueprintId) {
      // Validate current step before saving
      const isCurrentStepValid = await handleStepValidation();
      if (!isCurrentStepValid) {
        return;
      }

      try {
        await updateBlueprint({
          blueprintId,
          data: buildUpdatePayload(methods.getValues()),
        });
        navigate(`/solutions/${blueprintId}`);
      } catch (error) {
        console.error(`Error during skip and save:`, error);
        const axiosError = error as AxiosError<{ errors: Record<string, string>; detail?: string; message?: string }>;

        if (axiosError.response?.data?.errors) {
          setFieldErrors(axiosError.response.data.errors);
        }
      }
    }
  };

  return (
    <FormProvider {...methods}>
      <BlueprintStepTemplate
        currentStep={currentStepIndex}
        steps={WIZARD_STEPS.map((step) => step.label)}
        isFirstStep={currentStep.isFirst}
        isLastStep={currentStep.isLast}
        onNext={handleStepValidation}
        onPrevious={handlePrevious}
        onCancel={handleCancel}
        onSkipAndSave={handleSkipAndSave}
        showSkipButton={mode === 'edit'}
        submitButtonLabel={
          mode === 'edit' ? t('blueprints:wizard.navigation.save') : t('blueprints:wizard.navigation.create')
        }
        isSubmitting={mode === 'edit' ? isUpdatingBlueprint : isCreatingBlueprint}
      >
        <CurrentStepComponent
          formMethods={methods}
          blueprintId={blueprintId}
          mode={mode}
          onPowerProfileResetChange={setHasUserClickedPowerProfileReset}
        />
      </BlueprintStepTemplate>
    </FormProvider>
  );
};